/**
 * TaskAI assistant client.
 *
 * SECURITY: this used to call OpenAI directly from the browser with
 * REACT_APP_OPENAI_API_KEY inlined into the bundle (a live key leak). It now
 * calls our Cloudflare Worker, which holds the Anthropic key server-side and
 * runs Claude — no secret ever reaches the client. Same pattern as the guide
 * generation: GNK never talks to an LLM directly.
 */

import { supabase } from '../../api/db';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ChatResponse {
  message: string;
  tokensUsed?: number;
  model?: string;
}

const API_BASE =
  process.env.REACT_APP_API_URL || 'https://3rdeyefeel-api.gudimillanaveen.workers.dev';

export class ChatService {
  async sendMessage(userMessage: string, conversationHistory: ChatMessage[] = []): Promise<ChatResponse> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Please sign in to use TaskAI.');

    const res = await fetch(`${API_BASE}/api/assistant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message: userMessage, history: conversationHistory }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || 'TaskAI is unavailable right now. Please try again.');
    }
    return (await res.json()) as ChatResponse;
  }

  /** Always configured now — auth is checked at call time. */
  isConfigured(): boolean {
    return true;
  }
}

let chatServiceInstance: ChatService | null = null;

export function getChatService(): ChatService {
  if (!chatServiceInstance) chatServiceInstance = new ChatService();
  return chatServiceInstance;
}
